import type { GrammarRule } from './grammar'

/**
 * French grammar, from A1 to C2.
 *
 * What an English speaker finds hard in French is rarely word order, which is
 * close to English. It is the small words around the verb — ne…pas, object
 * pronouns, y and en — and the choices English doesn’t make at all: avoir or
 * être, imparfait or passé composé, indicative or subjunctive. The rules are
 * ordered so each of those arrives at the level where the islands start using
 * it.
 *
 * Typography follows French convention: a space before ? ! : and ;, written
 * here as a non-breaking space (\u00a0) so a phone never wraps the question
 * mark onto a line by itself. grammar.test.ts enforces it.
 */
export const GRAMMAR_FR: GrammarRule[] = [
  // ------------------------------------------------------------------ A1
  {
    id: 'fr-gender',
    level: 1,
    topic: 'nouns',
    title: 'Every noun is masculine or feminine',
    rule: 'French nouns have a gender, and the article shows it: le or un for masculine, la or une for feminine. Before a vowel sound, le and la both become l’, which hides the gender — one more reason to learn nouns with un or une.',
    pattern: [{ word: ['le', 'la', 'l’', 'un', 'une'] }, 'Noun'],
    examples: [
      { text: '[Le](le) [livre](Noun) est sur la table.', english: 'The book is on the table.' },
      { text: '[La](la) [voiture](Noun) est rouge.', english: 'The car is red.' },
      { text: '[L’](l’)[hôtel](Noun) est complet.', english: 'The hotel is full.' },
      { text: 'J’ai [une](une) [question](Noun).', english: 'I have a question.' },
    ],
    note: 'In the plural, les and des serve both genders.',
  },
  {
    id: 'fr-ne-pas',
    level: 1,
    topic: 'negation',
    title: 'ne…pas wraps around the verb',
    rule: 'To make a sentence negative, put ne before the conjugated verb and pas after it. Before a vowel, ne becomes n’.',
    pattern: [{ word: ['ne', 'n’'] }, 'Verb', { word: 'pas' }],
    examples: [
      { text: 'Je [ne](ne) [parle](Verb) [pas](pas) allemand.', english: 'I don’t speak German.' },
      { text: 'Il [n’](n’)[est](Verb) [pas](pas) là.', english: 'He isn’t here.' },
      { text: 'Nous [n’](n’)[avons](Verb) [pas](pas) de voiture.', english: 'We don’t have a car.' },
    ],
    pitfall: {
      wrong: 'Nous n’avons pas une voiture.',
      right: 'Nous n’avons pas de voiture.',
      why: 'After a negative, un, une and des become de.',
    },
    note: 'In everyday speech the ne often disappears — je parle pas allemand — but write it.',
  },
  {
    id: 'fr-adjective-position',
    level: 1,
    topic: 'nouns',
    title: 'Most adjectives follow the noun',
    rule: 'French adjectives usually come after the noun and agree with it. A short list of very common ones goes before: beau, joli, jeune, vieux, bon, mauvais, grand, petit, nouveau — beauty, age, goodness and size.',
    pattern: ['Noun', 'Adjective'],
    shape: 'contrast',
    examples: [
      { text: 'J’ai acheté une [voiture](Noun) [rouge](Adjective).', english: 'I bought a red car.' },
      { text: 'C’est un [livre](Noun) [intéressant](Adjective).', english: 'It’s an interesting book.' },
      { text: 'Ils habitent dans une [belle](Adjective) [maison](Noun).', english: 'They live in a beautiful house.' },
      { text: 'On se retrouve au [petit](Adjective) [café](Noun) du coin.', english: 'Let’s meet at the little café on the corner.' },
    ],
    pitfall: {
      wrong: 'J’ai acheté une rouge voiture.',
      right: 'J’ai acheté une voiture rouge.',
      why: 'Colors always follow the noun.',
    },
    note: 'Agreement shows in writing even when you can’t hear it: un livre intéressant, une histoire intéressante.',
  },
  {
    id: 'fr-questions',
    level: 1,
    topic: 'questions',
    title: 'Three ways to ask a question',
    rule: 'Raise your voice at the end of a statement, put est-ce que in front of it, or swap the subject and the verb. All three ask the same thing; they run from casual to formal.',
    pattern: ['Rising tone', { word: 'est-ce que' }, 'Inversion'],
    shape: 'contrast',
    examples: [
      { text: '[Tu viens ce soir](Rising tone)\u00a0?', english: 'Are you coming tonight? (casual)' },
      { text: '[Est-ce que](est-ce que) tu viens ce soir\u00a0?', english: 'Are you coming tonight? (neutral)' },
      { text: '[Viens-tu](Inversion) ce soir\u00a0?', english: 'Are you coming tonight? (formal)' },
      { text: 'Où [est-ce que](est-ce que) tu habites\u00a0?', english: 'Where do you live?' },
    ],
    note: 'Inversion joins the verb and subject with a hyphen, and adds -t- between two vowels, as in a-t-il.',
  },
  {
    id: 'fr-contractions',
    level: 1,
    topic: 'nouns',
    title: 'à le becomes au, de le becomes du',
    rule: 'The prepositions à and de merge with le and les: à + le = au, à + les = aux, de + le = du, de + les = des. With la and l’ they stay apart.',
    pattern: [{ word: ['au', 'aux', 'du', 'des'] }],
    shape: 'contrast',
    examples: [
      { text: 'Je vais [au](au) cinéma.', english: 'I’m going to the movies.' },
      { text: 'Il revient [du](du) bureau.', english: 'He’s coming back from the office.' },
      { text: 'Elle parle [aux](aux) enfants.', english: 'She’s talking to the children.' },
      { text: 'C’est la voiture [des](des) voisins.', english: 'It’s the neighbors’ car.' },
    ],
    pitfall: {
      wrong: 'Je vais à le cinéma.',
      right: 'Je vais au cinéma.',
      why: 'à and le always merge into au.',
    },
  },
  {
    id: 'fr-tu-vous',
    level: 1,
    topic: 'register',
    title: 'tu for friends, vous for everyone else — and for groups',
    rule: 'tu is for friends, family and children. vous is for strangers and anyone you would call Monsieur or Madame — and it is also the plural “you” for any group.',
    pattern: [{ word: 'tu' }, { word: 'vous' }],
    shape: 'contrast',
    examples: [
      { text: 'Comment [tu](tu) t’appelles\u00a0?', english: 'What’s your name? (to a friend)' },
      { text: 'Comment [vous](vous) appelez-vous\u00a0?', english: 'What’s your name? (to a stranger)' },
      { text: '[Tu](tu) veux un café\u00a0?', english: 'Do you want a coffee?' },
      { text: '[Vous](vous) voulez un café\u00a0?', english: 'Would you like a coffee?' },
    ],
    note: 'Wait for the other person to suggest switching to tu, often with on se tutoie.',
  },
  {
    id: 'fr-futur-proche',
    level: 1,
    topic: 'verbs',
    title: 'aller + infinitive: what’s going to happen',
    rule: 'Just like English “going to”, a present form of aller plus an infinitive talks about the near future. In conversation it is far more common than the future tense.',
    pattern: ['Form of aller', 'Infinitive'],
    examples: [
      { text: 'Je [vais](Form of aller) [partir](Infinitive) demain.', english: 'I’m going to leave tomorrow.' },
      { text: 'Il [va](Form of aller) [pleuvoir](Infinitive).', english: 'It’s going to rain.' },
      {
        text: 'Qu’est-ce que tu [vas](Form of aller) [faire](Infinitive) ce week-end\u00a0?',
        english: 'What are you going to do this weekend?',
      },
    ],
  },

  // ------------------------------------------------------------------ A2
  {
    id: 'fr-passe-compose',
    level: 2,
    topic: 'verbs',
    title: 'The past: avoir or être, then the participle',
    rule: 'The everyday past is a present form of avoir plus the past participle. About twenty verbs of movement and change — aller, venir, partir, arriver, naître, mourir — and every reflexive verb use être, and then the participle agrees with the subject.',
    pattern: ['Auxiliary', 'Participle'],
    examples: [
      { text: 'J’[ai](Auxiliary) [mangé](Participle) à midi.', english: 'I ate at noon.' },
      { text: 'Elle [est](Auxiliary) [parti**e**](Participle) hier.', english: 'She left yesterday.' },
      { text: 'Nous [sommes](Auxiliary) [arrivé**s**](Participle) en retard.', english: 'We arrived late.' },
      { text: 'Ils se [sont](Auxiliary) [levé**s**](Participle) tôt.', english: 'They got up early.' },
    ],
    pitfall: {
      wrong: 'J’ai allé au marché.',
      right: 'Je suis allé au marché.',
      why: 'aller is one of the verbs that take être. Not every verb of motion does — marcher and courir take avoir — so the list is worth learning.',
    },
    note: 'With être the participle agrees like an adjective: il est parti, elle est partie, ils sont partis.',
  },
  {
    id: 'fr-object-pronouns',
    level: 2,
    topic: 'verbs',
    title: 'Object pronouns go before the verb',
    rule: 'le, la, les, lui and leur come directly before the conjugated verb — or before the infinitive, if there is one.',
    pattern: ['Pronoun', 'Verb'],
    examples: [
      { text: 'Je [le](Pronoun) [vois](Verb) demain.', english: 'I’m seeing him tomorrow.' },
      { text: 'Tu [lui](Pronoun) [as parlé](Verb)\u00a0?', english: 'Did you talk to her?' },
      { text: 'Je ne [la](Pronoun) [connais](Verb) pas.', english: 'I don’t know her.' },
      { text: 'Je vais [les](Pronoun) [appeler](Verb).', english: 'I’m going to call them.' },
    ],
    pitfall: {
      wrong: 'Je vois le demain.',
      right: 'Je le vois demain.',
      why: 'English puts the pronoun after the verb. French puts it in front.',
    },
    note: 'In the past tense it goes before avoir: je l’ai vu.',
  },
  {
    id: 'fr-partitive',
    level: 2,
    topic: 'nouns',
    title: 'du, de la, des: some of something',
    rule: 'French needs an article where English has none. For an unspecified amount of something — bread, water, patience — use du, de la or de l’.',
    pattern: [{ word: ['du', 'de la', 'de l’', 'des'] }, 'Noun'],
    examples: [
      { text: 'Je voudrais [du](du) [pain](Noun).', english: 'I’d like some bread.' },
      { text: 'Tu veux [de l’](de l’)[eau](Noun)\u00a0?', english: 'Do you want some water?' },
      { text: 'Il faut [de la](de la) [patience](Noun).', english: 'It takes patience.' },
      { text: 'On a acheté [des](des) [fraises](Noun).', english: 'We bought some strawberries.' },
    ],
    pitfall: {
      wrong: 'Je voudrais pain.',
      right: 'Je voudrais du pain.',
      why: 'A French noun almost never stands alone. Where English says “bread”, French says du pain.',
    },
    note: 'After a negative, all of these become de: je ne veux pas de pain.',
  },

  // ------------------------------------------------------------------ B1
  {
    id: 'fr-imparfait-passe-compose',
    level: 3,
    topic: 'verbs',
    title: 'Imparfait for the background, passé composé for events',
    rule: 'The imparfait describes how things were: ongoing states, habits, the scene. The passé composé tells what happened: completed events that move the story forward.',
    pattern: ['Imparfait', 'Passé composé'],
    shape: 'contrast',
    examples: [
      {
        text: 'Quand j’[étais](Imparfait) petit, j’[habitais](Imparfait) à Lyon.',
        english: 'When I was little, I lived in Lyon.',
      },
      { text: 'Je [lisais](Imparfait) quand le téléphone [a sonné](Passé composé).', english: 'I was reading when the phone rang.' },
      {
        text: 'Il [faisait](Imparfait) beau, alors nous [sommes sortis](Passé composé).',
        english: 'The weather was nice, so we went out.',
      },
    ],
    note: 'A useful test: if English could say “was …ing” or “used to”, French wants the imparfait.',
  },
  {
    id: 'fr-y-en',
    level: 3,
    topic: 'nouns',
    title: 'y replaces a place, en replaces a quantity',
    rule: 'y stands in for à + a place or thing: j’y vais, “I’m going there”. en stands in for de + something, and for any quantity: j’en ai deux, “I have two of them”. Both go before the verb.',
    pattern: [{ word: ['y', 'en'] }, 'Verb'],
    examples: [
      {
        text: 'Tu vas à Paris\u00a0? Oui, j’[y](y) [vais](Verb) demain.',
        english: 'Are you going to Paris? Yes, I’m going there tomorrow.',
      },
      { text: 'Des enfants\u00a0? J’[en](en) [ai](Verb) deux.', english: 'Kids? I have two.' },
      {
        text: 'Tu veux du café\u00a0? Non merci, j’[en](en) [ai](Verb) déjà bu.',
        english: 'Do you want some coffee? No thanks, I’ve already had some.',
      },
      { text: 'On [y](y) [pense](Verb) souvent.', english: 'We often think about it.' },
    ],
    note: 'Where English can drop “of them”, French can’t: j’en ai deux, never j’ai deux.',
  },
  {
    id: 'fr-relative-pronouns',
    level: 3,
    topic: 'nouns',
    title: 'qui, que and dont',
    rule: 'qui is the subject of its clause, que is the object, and dont replaces de + something: le film dont je t’ai parlé, “the movie I told you about”.',
    pattern: ['Noun', { word: ['qui', 'que', 'dont'] }],
    examples: [
      { text: 'C’est [l’homme](Noun) [qui](qui) habite à côté.', english: 'That’s the man who lives next door.' },
      { text: '[Le livre](Noun) [que](que) je lis est passionnant.', english: 'The book I’m reading is fascinating.' },
      { text: 'C’est [le film](Noun) [dont](dont) je t’ai parlé.', english: 'That’s the movie I told you about.' },
    ],
    pitfall: {
      wrong: 'Le livre qui je lis est passionnant.',
      right: 'Le livre que je lis est passionnant.',
      why: 'je is already the subject of lis, so the pronoun is its object: que.',
    },
    note: 'English often leaves out “that”. French never leaves out que.',
  },
  {
    id: 'fr-si-conditional',
    level: 3,
    topic: 'verbs',
    title: 'si with the imparfait, then the conditional',
    rule: 'For something hypothetical, the si half takes the imparfait and the other half takes the conditional. The conditional also softens a request: je voudrais.',
    pattern: [{ word: 'si' }, 'Imparfait', 'Conditional'],
    examples: [
      {
        text: '[Si](si) j’[avais](Imparfait) le temps, je [voyagerais](Conditional) plus.',
        english: 'If I had the time, I’d travel more.',
      },
      {
        text: '[S’](si)il [faisait](Imparfait) beau, on [irait](Conditional) à la plage.',
        english: 'If the weather were nice, we’d go to the beach.',
      },
      { text: 'Je [voudrais](Conditional) un café, s’il vous plaît.', english: 'I’d like a coffee, please.' },
    ],
    pitfall: {
      wrong: 'Si j’aurais le temps, je voyagerais.',
      right: 'Si j’avais le temps, je voyagerais.',
      why: 'Never the conditional straight after si. It belongs in the other half.',
    },
  },

  // ------------------------------------------------------------------ B2
  {
    id: 'fr-subjunctive',
    level: 4,
    topic: 'verbs',
    title: 'The subjunctive after needing, wanting and feeling',
    rule: 'After que, the subjunctive follows expressions of necessity, will, emotion and doubt: il faut que, je veux que, je suis content que, bien que.',
    pattern: ['Trigger', { word: 'que' }, 'Subjunctive'],
    examples: [
      { text: '[Il faut](Trigger) [que](que) tu [viennes](Subjunctive).', english: 'You have to come.' },
      { text: '[Je veux](Trigger) [que](que) tu [saches](Subjunctive) la vérité.', english: 'I want you to know the truth.' },
      { text: '[Bien](Trigger) [qu’](que)il [soit](Subjunctive) tard, on continue.', english: 'Even though it’s late, we’re carrying on.' },
    ],
    pitfall: {
      wrong: 'Il faut que tu viens.',
      right: 'Il faut que tu viennes.',
      why: 'il faut que always takes the subjunctive.',
    },
    note: 'je pense que takes the indicative, but je ne pense pas que takes the subjunctive: the doubt is the trigger.',
  },
  {
    id: 'fr-gerondif',
    level: 4,
    topic: 'verbs',
    title: 'en + -ant: while doing, by doing',
    rule: 'en plus the present participle says two things happen at once, or how something is done. The subject is the same for both actions.',
    pattern: [{ word: 'en' }, 'Present participle'],
    examples: [
      { text: 'Il chante [en](en) [travaill**ant**](Present participle).', english: 'He sings while he works.' },
      {
        text: '[En](en) [part**ant**](Present participle) tôt, on évitera les bouchons.',
        english: 'If we leave early, we’ll avoid the traffic.',
      },
      {
        text: 'J’ai appris le français [en](en) [regard**ant**](Present participle) des films.',
        english: 'I learned French by watching movies.',
      },
    ],
    note: 'The subject has to match: en sortant, j’ai vu Marie means I was the one going out.',
  },
  {
    id: 'fr-ce-qui-ce-que',
    level: 4,
    topic: 'nouns',
    title: 'ce qui and ce que: what',
    rule: 'When “what” means “the thing that”, French says ce qui if it is the subject of its clause, and ce que if it is the object.',
    pattern: [{ word: 'ce qui' }, { word: 'ce que' }],
    shape: 'contrast',
    examples: [
      { text: '[Ce qui](ce qui) m’intéresse, c’est l’histoire.', english: 'What interests me is history.' },
      { text: 'Dis-moi [ce que](ce que) tu veux.', english: 'Tell me what you want.' },
      { text: 'Je ne comprends pas [ce qui](ce qui) se passe.', english: 'I don’t understand what’s going on.' },
    ],
    pitfall: {
      wrong: 'Dis-moi que tu veux.',
      right: 'Dis-moi ce que tu veux.',
      why: 'que on its own means “that”. For “what”, French needs ce que.',
    },
  },

  // ------------------------------------------------------------------ C1
  {
    id: 'fr-cleft',
    level: 5,
    topic: 'order',
    title: 'C’est… qui, c’est… que: the spotlight',
    rule: 'French rarely stresses a word with the voice. It moves the word into c’est… qui or c’est… que instead: C’est moi qui ai payé, “I’m the one who paid”.',
    pattern: [{ word: 'c’est' }, 'Focus', { word: ['qui', 'que'] }],
    examples: [
      { text: '[C’est](c’est) [moi](Focus) [qui](qui) ai payé.', english: 'I’m the one who paid.' },
      { text: '[C’est](c’est) [à Paris](Focus) [que](que) je l’ai rencontrée.', english: 'It was in Paris that I met her.' },
      { text: '[C’est](c’est) [demain](Focus) [que](que) tu pars\u00a0?', english: 'Is it tomorrow that you’re leaving?' },
    ],
    note: 'After c’est moi qui the verb agrees with moi: c’est moi qui ai payé, not qui a payé.',
  },
  {
    id: 'fr-avoir-agreement',
    level: 5,
    topic: 'verbs',
    title: 'Agreement with avoir: when the object comes first',
    rule: 'With avoir the past participle normally doesn’t agree. It does agree with a direct object that comes before it — usually a pronoun, or que.',
    pattern: ['Object before', 'Participle'],
    examples: [
      {
        text: 'Les photos [que](Object before) j’ai [pris**es**](Participle) sont belles.',
        english: 'The photos I took are beautiful.',
      },
      { text: 'Tes clés\u00a0? Je [les](Object before) ai [vu**es**](Participle) sur la table.', english: 'Your keys? I saw them on the table.' },
      { text: 'Ces lettres, je [les](Object before) ai [écrit**es**](Participle) hier.', english: 'Those letters — I wrote them yesterday.' },
    ],
    pitfall: {
      wrong: 'Les photos que j’ai pris sont belles.',
      right: 'Les photos que j’ai prises sont belles.',
      why: 'que stands for les photos, feminine plural, and it comes before the participle.',
    },
    note: 'Often you can’t hear it — vues sounds like vu — but prises and écrites you can.',
  },
  {
    id: 'fr-passe-simple',
    level: 5,
    topic: 'register',
    title: 'The passé simple: the past of novels and history',
    rule: 'Written narrative uses the passé simple for completed events where speech would use the passé composé. You will rarely say it, but you will read it constantly.',
    pattern: ['Passé simple'],
    examples: [
      {
        text: 'Il [entra](Passé simple), [s’assit](Passé simple) et [attendit](Passé simple).',
        english: 'He came in, sat down and waited.',
      },
      { text: 'Victor Hugo [naquit](Passé simple) à Besançon en 1802.', english: 'Victor Hugo was born in Besançon in 1802.' },
      { text: 'La guerre [dura](Passé simple) quatre ans.', english: 'The war lasted four years.' },
    ],
    note: 'Mostly you need to recognize the third person: -a, -it, -ut, and -èrent, -irent, -urent in the plural.',
  },

  // ------------------------------------------------------------------ C2
  {
    id: 'fr-ne-expletif',
    level: 6,
    topic: 'register',
    title: 'The ne that doesn’t negate',
    rule: 'In careful French, a ne without pas appears after verbs of fearing, after avant que and à moins que, and in comparisons. It carries no negative meaning at all.',
    pattern: ['Trigger', { word: 'ne' }],
    examples: [
      { text: '[Je crains](Trigger) qu’il [ne](ne) soit trop tard.', english: 'I’m afraid it may be too late.' },
      { text: 'Partons [avant](Trigger) qu’il [ne](ne) pleuve.', english: 'Let’s go before it rains.' },
      { text: 'C’est [plus difficile](Trigger) que je [ne](ne) le pensais.', english: 'It’s harder than I thought.' },
    ],
    note: 'Leaving it out is never wrong, and most people do in speech. Adding pas, though, would reverse the meaning.',
  },
  {
    id: 'fr-superlative-subjunctive',
    level: 6,
    topic: 'verbs',
    title: 'The subjunctive after a superlative',
    rule: 'After le seul, le premier, le meilleur and other superlatives, a relative clause takes the subjunctive, marking the claim as the speaker’s judgment.',
    pattern: ['Superlative', { word: ['que', 'qui'] }, 'Subjunctive'],
    examples: [
      {
        text: 'C’est [le meilleur film](Superlative) [que](que) j’[aie](Subjunctive) jamais vu.',
        english: 'It’s the best movie I’ve ever seen.',
      },
      {
        text: 'Tu es [la seule personne](Superlative) [qui](qui) [puisse](Subjunctive) m’aider.',
        english: 'You’re the only person who can help me.',
      },
    ],
    note: 'In speech the indicative is heard here too; the subjunctive is what careful French expects.',
  },
]
